import { BaseDataProvider } from "./baseProvider";
import type { UserListParams } from "../types/api";

/**
 * Normalize an API user record for the admin UI.
 * The API returns `firstName` / `lastName` (no `name`) and `avatarUrl` (no `avatar`),
 * and does not send a derived `isOAuthAccount` on admin endpoints (only on login).
 * Components bind to `name` / `isOAuthAccount`, so compute them here — one place —
 * rather than touching every field reference.
 */
const mapUser = (u: unknown): unknown => {
  if (!u || typeof u !== "object") return u;
  const user = u as Record<string, unknown>;
  const first = (user.firstName as string) || "";
  const last = (user.lastName as string) || "";
  const name = [first, last].filter(Boolean).join(" ").trim();
  return {
    ...user,
    name: name || (user.username as string) || "",
    isOAuthAccount:
      user.isOAuthAccount ?? Boolean(user.googleId || user.appleId),
  };
};

export class UserDataProvider extends BaseDataProvider {
  constructor() {
    super("users");
  }

  async getList(_resource: string, params: UserListParams) {
    const { page, perPage } = params.pagination || { page: 1, perPage: 20 };
    const { field, order } = params.sort || {
      field: "createdAt",
      order: "DESC",
    };
    const filter = params.filter || {};

    // Build query parameters for users API
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: perPage.toString(),
      ...(field && { sortBy: field }),
      ...(order && { sortOrder: order.toUpperCase() }),
      ...(filter.search && { search: filter.search }),
      ...(filter.role && { role: filter.role }),
      ...(filter.onboardingCompleted !== undefined && {
        onboardingCompleted: filter.onboardingCompleted.toString(),
      }),
    });

    const json = await this.makeRequest(`users?${queryParams}`);

    // Handle different possible API response structures
    const apiResponse = json as {
      data?: unknown[];
      users?: unknown[];
      total?: number;
      totalCount?: number;
      pageInfo?: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
      };
    };

    // Use users array if data array is not available
    const dataArray = (apiResponse.data || apiResponse.users || []).map(
      mapUser,
    );
    const total = apiResponse.total || apiResponse.totalCount || 0;

    return {
      data: dataArray,
      total: total,
      pageInfo: {
        hasNextPage: apiResponse.pageInfo?.hasNextPage || false,
        hasPreviousPage: apiResponse.pageInfo?.hasPreviousPage || false,
      },
    };
  }

  async getOne(_resource: string, params: Record<string, unknown>) {
    const endpoint = `users/${params.id}`;
    const response = await this.makeRequest(endpoint);
    return { data: mapUser(response) };
  }

  async getMany(resource: string, params: Record<string, unknown>) {
    // For getMany, we'll fetch each user individually
    const promises = (params.ids as string[]).map((id) =>
      this.getOne(resource, { id }),
    );
    const results = await Promise.all(promises);
    return { data: results.map((result) => result.data) };
  }

  async getManyReference() {
    // This would typically be used for related resources
    return {
      data: [],
      total: 0,
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }

  // Users support create, update, and delete operations
  async create(_resource: string, params: Record<string, unknown>) {
    const endpoint = "users";
    const response = await this.makeRequest(endpoint, {
      method: "POST",
      body: JSON.stringify(params.data),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });
    return { data: mapUser(response) };
  }

  async update(_resource: string, params: Record<string, unknown>) {
    const meta = params.meta as
      | { endpoint?: string; method?: string }
      | undefined;
    const endpoint = meta?.endpoint ?? `users/${params.id}`;
    const response = await this.makeRequest(endpoint, {
      method: meta?.method ?? "PUT",
      body: JSON.stringify(params.data),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });
    // Some endpoints (lock, reset-password) return { success: true } with no record —
    // fall back to the id so react-admin's update cycle doesn't choke.
    const data =
      response && typeof response === "object" && "id" in (response as object)
        ? mapUser(response)
        : { id: params.id, ...(params.data as object) };
    return { data };
  }

  async updateMany(_resource: string, params: Record<string, unknown>) {
    // For bulk updates, we'll update each user individually
    const promises = (params.ids as string[]).map((id) =>
      this.update(_resource, { id, data: params.data }),
    );
    const results = await Promise.all(promises);
    return { data: results.map((result) => result.data) };
  }

  async delete(_resource: string, params: Record<string, unknown>) {
    const endpoint = `users/${params.id}`;
    await this.makeRequest(endpoint, {
      method: "DELETE",
    });
    return { data: params.id };
  }

  async deleteMany(_resource: string, params: Record<string, unknown>) {
    // For bulk deletes, we'll delete each user individually
    const promises = (params.ids as string[]).map((id) =>
      this.delete(_resource, { id }),
    );
    await Promise.all(promises);
    return { data: params.ids };
  }
}
